import React, { useEffect } from "react";
import { db, auth } from '../firebaseConfig';
import { fetchFollowUps, FollowUpWithUserData, fetchRemarks, RemarkWithUserData, fetchEmails, EmailWithUserData, fetchTransferLogs, TransferLogWithUserData } from '../pages/Clients/Common/Functions';
import { useParams } from 'react-router-dom';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';


interface UserData {
  id: string;
  companyId?: string;
  // Add other user fields here
  [key: string]: any; // For other user fields
}

interface CombinedData {
  id: string;
  text: string;
  time: Timestamp;
  updatedAt?: Timestamp; // For follow-ups
  createdAt?: Timestamp; // For remarks, emails, and transfer logs
  type: string; // 'followUp', 'remark', 'email', or 'transferLog'
  userId?: string;
  isDone?: boolean;
  isEdited?: boolean;
  userName?: string;
  // Transfer log specific fields
  exAssignedTo?: string;
  exAssignedName?: string;
  newAssignedTo?: string;
  newAssignedName?: string;
}

const FollowUps = () => {

  const { currentSubAgent } = useSubAgentAuth();

  const [followUps, setFollowUps] = React.useState<FollowUpWithUserData[]>([]);
  const [remarks, setRemarks] = React.useState<RemarkWithUserData[]>([]);
  const [emails, setEmails] = React.useState<EmailWithUserData[]>([]);
  const [transferLogs, setTransferLogs] = React.useState<TransferLogWithUserData[]>([]);

  // Combined data state
  const [combinedData, setCombinedData] = React.useState<CombinedData[]>([]);

  const [lastDocumentFollowUp, setLastDocumentFollowUp] = React.useState<{
    id: string;
    lastFollowUpTime: Timestamp;
  } | null>(null);
  const [lastRemarkDocument, setLastRemarkDocument] = React.useState<{
    id: string;
    lastRemarkDate: Timestamp;
  } | null>(null);
  const [lastEmailDocument, setLastEmailDocument] = React.useState<{
    id: string;
    lastEmailDate: Timestamp;
  } | null>(null);
  const [lastTransferLogDocument, setLastTransferLogDocument] = React.useState<{
    id: string;
    lastTransferDate: Timestamp;
  } | null>(null);

  const [isLoadingFollowUps, setIsLoadingFollowUps] = React.useState(false);
  const [isLoadingRemarks, setIsLoadingRemarks] = React.useState(false);
  const [isLoadingEmails, setIsLoadingEmails] = React.useState(false);
  const [isLoadingTransferLogs, setIsLoadingTransferLogs] = React.useState(false);
  const [hasMoreFollowUps, setHasMoreFollowUps] = React.useState(true);
  const [hasMoreRemarks, setHasMoreRemarks] = React.useState(true);
  const [hasMoreEmails, setHasMoreEmails] = React.useState(true);
  const [hasMoreTransferLogs, setHasMoreTransferLogs] = React.useState(true);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const getStatusStyles = (isDone?: boolean, isEdited?: boolean) => {
    // If both isEdited and isDone are true, show "Edited" with blue color
    if (isEdited && isDone) {
      return {
        color: "text-blue-600",
        text: "Edited"
      };
    }
    // If only isDone is true (and isEdited is false or undefined), show "Completed"
    if (isDone && !isEdited) {
      return {
        color: "text-green-600",
        text: "Completed"
      };
    }
    // If neither isDone nor isEdited is true, show "Pending"
    if (!isDone) {
      return {
        color: "text-red-600",
        text: "Pending"
      };
    }
    // Default case
    return {
      color: "",
      text: "",
    };
  };

  const leadId = useParams<{ leadId: string }>().leadId;

  const fetchFollowUpsData = async (isLoadingMore = false) => {
    const user = auth.currentUser;
    if (!user || isLoadingFollowUps || (!hasMoreFollowUps && isLoadingMore)) {
      return;
    }

    let userData: UserData | null = null;

    // Get user document
    if (currentSubAgent) {
      console.log('currentSubAgent', currentSubAgent.companyId, currentSubAgent.subAgentId);
      if (currentSubAgent.companyId && currentSubAgent.subAgentId) {
        const userDoc = await getDoc(doc(db, 'companies', currentSubAgent.companyId, 'subAgents', currentSubAgent.subAgentId));
        userData = userDoc.data() as UserData;
        console.log('userData', userData);

        if (!userData) {
          console.error('User data not found');
          return;
        }
      } else {
        console.error('Missing companyId or subAgentId in currentSubAgent');
        return;
      }
    } else {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      userData = userDoc.data() as UserData;
      if (!userData) {
        console.error('User data not found');
        return;
      }
    }

    setIsLoadingFollowUps(true);
    try {
      const response = await fetchFollowUps(db, userData, {
        leadId,
        pageSize: 100, // Increased from 3 to 20
        ...(isLoadingMore && lastDocumentFollowUp && {
          lastDocumentId: lastDocumentFollowUp.id,
          lastFollowUpDate: lastDocumentFollowUp.lastFollowUpTime,
        }),
      });

      if (response.data.hasMore === false) {
        setHasMoreFollowUps(false);
      }

      // Update last document for pagination
      const lastFollowUp = response.data.lastDocument
      if (lastFollowUp) {
        setLastDocumentFollowUp({
          id: lastFollowUp.id,
          lastFollowUpTime: lastFollowUp?.lastFollowUpDate,
        });
      }

      // Sort followUps by updatedAt (creation time - most recent first) before setting state
      const sortedFollowUps = response.data.followUps.sort((a, b) => {
        const aTime = (a as any).createdAt || a.time;
        const bTime = (b as any).createdAt || b.time;
        return (bTime as Timestamp).toMillis() - (aTime as Timestamp).toMillis();
      });

      // Append new followUps to existing ones if loading more
      setFollowUps(prev =>
        isLoadingMore ? [...prev, ...sortedFollowUps] : sortedFollowUps
      );
    } catch (error) {
      console.error('Error fetching follow ups:', error);
    } finally {
      setIsLoadingFollowUps(false);
    }
  }

  const fetchRemarksData = async (isLoadingMore = false) => {
    const user = auth.currentUser;
    if (!user || isLoadingRemarks || (!hasMoreRemarks && isLoadingMore)) {
      return;
    }

    let userData: UserData | null = null;
    // Get user document
    if (currentSubAgent) {
      console.log('currentSubAgent', currentSubAgent.companyId, currentSubAgent.subAgentId);
      if (currentSubAgent.companyId && currentSubAgent.subAgentId) {
        const userDoc = await getDoc(doc(db, 'companies', currentSubAgent.companyId, 'subAgents', currentSubAgent.subAgentId));
        userData = userDoc.data() as UserData;
        console.log('userData', userData);
        if (!userData) {
          console.error('User data not found');
          return;
        }
      } else {
        console.error('Missing companyId or subAgentId in currentSubAgent');
        return;
      }
    } else {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      userData = userDoc.data() as UserData;
      if (!userData) {
        console.error('User data not found');
        return;
      }
    }

    setIsLoadingRemarks(true);
    try {
      const response = await fetchRemarks(db, userData, {
        leadId,
        pageSize: 20, // Increased from 3 to 20
        ...(isLoadingMore && lastRemarkDocument && {
          lastDocumentId: lastRemarkDocument.id,
          lastRemarkDate: lastRemarkDocument.lastRemarkDate,
        }),
      });

      if (response.data.hasMore === false) {
        setHasMoreRemarks(false);
      }

      // Update last document for pagination
      const lastRemark = response.data.lastDocument
      if (lastRemark) {
        setLastRemarkDocument({
          id: lastRemark.id,
          lastRemarkDate: lastRemark?.lastRemarkDate,
        });
      }

      // Sort remarks by createdAt/time (most recent first) before setting state
      const sortedRemarks = response.data.remarks.sort((a, b) => {
        const aTime = (a as any).createdAt || a.time;
        const bTime = (b as any).createdAt || b.time;
        return (bTime as Timestamp).toMillis() - (aTime as Timestamp).toMillis();
      });

      // Append new remarks to existing ones if loading more
      setRemarks(prev =>
        isLoadingMore ? [...prev, ...sortedRemarks] : sortedRemarks
      );
    } catch (error) {
      console.error('Error fetching remarks:', error);
    } finally {
      setIsLoadingRemarks(false);
    }
  };

  // fetch the Transfer logs
  const fetchTransferLogsData = async (isLoadingMore = false) => {
    const user = auth.currentUser;
    if (!user || isLoadingTransferLogs || (!hasMoreTransferLogs && isLoadingMore)) {
      return;
    }

    let userData: UserData | null = null;
    // Get user document
    if (currentSubAgent) {
      console.log('currentSubAgent', currentSubAgent.companyId, currentSubAgent.subAgentId);
      if (currentSubAgent.companyId && currentSubAgent.subAgentId) {
        const userDoc = await getDoc(doc(db, 'companies', currentSubAgent.companyId, 'subAgents', currentSubAgent.subAgentId));
        userData = userDoc.data() as UserData;
        console.log('userData', userData);
        if (!userData) {
          console.error('User data not found');
          return;
        }
      } else {
        console.error('Missing companyId or subAgentId in currentSubAgent');
        return;
      }
    } else {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      userData = userDoc.data() as UserData;
      if (!userData) {
        console.error('User data not found');
        return;
      }
    }

    setIsLoadingTransferLogs(true);
    try {
      const response = await fetchTransferLogs(db, userData, {
        leadId,
        pageSize: 10,
        ...(isLoadingMore && lastTransferLogDocument && {
          lastDocumentId: lastTransferLogDocument.id,
          lastTransferDate: lastTransferLogDocument.lastTransferDate,
        }),
      });

      if (response.data.hasMore === false) {
        setHasMoreTransferLogs(false);
      }

      // Update last document for pagination
      const lastTransferLog = response.data.lastDocument;
      if (lastTransferLog) {
        setLastTransferLogDocument({
          id: lastTransferLog.id,
          lastTransferDate: lastTransferLog.lastTransferDate,
        });
      }

      // Sort transfer logs by createdAt/time (most recent first) before setting state
      const sortedTransferLogs = response.data.transferLogs.sort((a, b) => {
        const aTime = (a as any).createdAt || a.time;
        const bTime = (b as any).createdAt || b.time;
        return (bTime as Timestamp).toMillis() - (aTime as Timestamp).toMillis();
      });

      // Append new transfer logs to existing ones if loading more
      setTransferLogs(prev =>
        isLoadingMore ? [...prev, ...sortedTransferLogs] : sortedTransferLogs
      );
    } catch (error) {
      console.error('Error fetching transfer logs:', error);
    } finally {
      setIsLoadingTransferLogs(false);
    }
  };

  const fetchEmailsData = async (isLoadingMore = false) => {
    const user = auth.currentUser;
    if (!user || isLoadingEmails || (!hasMoreEmails && isLoadingMore)) {
      return;
    }
    let userData: UserData | null = null;
    // Get user document
    if (currentSubAgent) {
      console.log('currentSubAgent', currentSubAgent.companyId, currentSubAgent.subAgentId);
      if (currentSubAgent.companyId && currentSubAgent.subAgentId) {
        const userDoc = await getDoc(doc(db, 'companies', currentSubAgent.companyId, 'subAgents', currentSubAgent.subAgentId));
        userData = userDoc.data() as UserData;
        console.log('userData', userData);
        if (!userData) {
          console.error('User data not found');
          return;
        }
      } else {
        console.error('Missing companyId or subAgentId in currentSubAgent');
        return;
      }
    } else {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      userData = userDoc.data() as UserData;
      if (!userData) {
        console.error('User data not found');
        return;
      }
    }
    setIsLoadingEmails(true);
    try {
      const response = await fetchEmails(db, userData, {
        leadId,
        pageSize: 10,
        ...(isLoadingMore && lastEmailDocument && {
          lastDocumentId: lastEmailDocument.id,
          lastEmailDate: lastEmailDocument.lastEmailDate,
        }),
      });
      if (response.data.hasMore === false) {
        setHasMoreEmails(false);
      }
      // Update last document for pagination
      const lastEmail = response.data.lastDocument;
      if (lastEmail) {
        setLastEmailDocument({
          id: lastEmail.id,
          lastEmailDate: lastEmail.lastEmailDate,
        });
      }
      // Sort emails by createdAt/time (most recent first) before setting state
      const sortedEmails = response.data.emails.sort((a, b) => {
        const aTime = (a as any).createdAt || a.time;
        const bTime = (b as any).createdAt || b.time;
        return (bTime as Timestamp).toMillis() - (aTime as Timestamp).toMillis();
      });

      // Append new emails to existing ones if loading more
      setEmails(prev =>
        isLoadingMore ? [...prev, ...sortedEmails] : sortedEmails
      );
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setIsLoadingEmails(false);
    }
  };


  // Handle scroll events
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollPosition = container.scrollTop + container.clientHeight;
    const scrollThreshold = container.scrollHeight - 200; // Load more when within 200px of bottom

    if (scrollPosition >= scrollThreshold) {
      // Only load more if there's more data available and not currently loading
      if (hasMoreFollowUps && !isLoadingFollowUps) {
        fetchFollowUpsData(true);
      }
      if (hasMoreRemarks && !isLoadingRemarks) {
        fetchRemarksData(true);
      }
      if (hasMoreEmails && !isLoadingEmails) {
        fetchEmailsData(true);
      }
      if (hasMoreTransferLogs && !isLoadingTransferLogs) {
        fetchTransferLogsData(true);
      }
    }
  };

  useEffect(() => {
    fetchFollowUpsData();
    fetchRemarksData();
    fetchEmailsData();
    fetchTransferLogsData();
  }, []);

  useEffect(() => {
    // Since each data type is already sorted by createdAt/time in their respective fetch functions,
    // we just need to combine them and sort the combined result by createdAt
    const combinedData = [...followUps, ...remarks, ...emails, ...transferLogs];

    // Remove duplicates based on ID (assuming each item has a unique id)
    const uniqueData = combinedData.filter((item, index, self) =>
      index === self.findIndex(t => t.id === item.id)
    );

    // Sort all data by their createdAt field (most recent first)
    // Use createdAt for follow-ups, createdAt for others, fallback to time
    const sortedData = uniqueData.sort((a, b) => {
      const aTime = a.type === 'followUp' 
        ? ((a as any).createdAt || a.time)
        : ((a as any).createdAt || a.time);
      const bTime = b.type === 'followUp' 
        ? ((b as any).createdAt || b.time)
        : ((b as any).createdAt || b.time);
      return (bTime as Timestamp).toMillis() - (aTime as Timestamp).toMillis();
    });

    console.log('sortedData', sortedData);
    
    setCombinedData(sortedData);
  }, [followUps, remarks, emails, transferLogs]);
  
  function formatFirebaseTimestamp(timestamp: Timestamp | any) {
    if (!timestamp || typeof timestamp.seconds !== 'number') {
      return '';
    }

    // Convert seconds to milliseconds
    const date = new Date(timestamp.seconds * 1000);

    // Options for formatting the date
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };

    // Format the date using Intl.DateTimeFormat
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

    // Replace commas with the desired separator
    return formattedDate.replace(',', ' •');
  }

  return (
    <div className="w-full h-[650px] max-w-3xl mx-auto my-[0] bg-white rounded-3xl shadow-sm border border-gray-200 flex flex-col" style={{ fontFamily: "Montserrat" }}>
      <style>
        {`
                .follow-ups-scroll::-webkit-scrollbar {
                    width: 13px;
                    border-radius: 30px;
                    height: 60px;
                }

                .follow-ups-scroll::-webkit-scrollbar-track {
                    background: #DAEBF3;
                    border-radius: 30px;
                }

                .follow-ups-scroll::-webkit-scrollbar-thumb {
                    background-color: #82b3c2;
                    border-radius: 30px;
                }

                .follow-ups-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: #82b3c2 #DAEBF3;
                    -ms-overflow-style: none;
                    overflow-y: auto;
                    border-radius: 30px;
                    overflow-x: hidden;
                }

                .follow-ups-scroll::-webkit-scrollbar-button {
                    display: none;
                    border-radius: 30px;
                }
                `}
      </style>

      <div className="bg-gray-100 p-4 flex items-center justify-center text-center rounded-t-3xl h-8">
        <h1 className="text-m text-black font-medium" style={{ lineHeight: "29.26px" }}>
          Follow Ups, Remarks & Transfer Logs
        </h1>
      </div>

      <div className="flex-1 follow-ups-scroll overflow-y-auto" ref={scrollContainerRef} onScroll={handleScroll}>
        <div className="p-4 min-h-full">
          <div className="relative min-h-[400px]">
            <div className="absolute left-1/2 top-0 h-full w-px bg-blue-500" />
            <div className="space-y-6">
              {combinedData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No follow-ups, remarks, emails, or transfer logs found.
                </div>
              ) : (
                combinedData.map((data, index) => (
                <div
                  key={data.id}
                  className={`relative flex ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}>
                  <div className="absolute left-1/2 -ml-1.5">
                    <div className="w-3 h-3 rounded-full bg-white border-2 border-blue-500" />
                  </div>

                  <div className={`w-1/2 ${index % 2 === 0 ? "pr-8" : "pl-8"}`}>
                    <div className={`${index % 2 === 0 ? "text-right" : "text-left"}`}>
                      <div className="text-xs text-gray-500" style={{ fontFamily: "Montserrat" }}>
                        <span>{formatFirebaseTimestamp((data as any).createdAt || data.time)}</span>
                      </div>

                      <div className="mt-1 bg-white rounded-xl border border-gray-200 p-3 hover:shadow-sm transition-shadow">

                        <h3 className="font-semibold text-xs text-gray-900">
                          <span className={`${
                            data.type === 'followUp' ? 'text-blue-500' : 
                            data.type === 'remark' ? 'text-yellow-500' : 
                            data.type === 'email' ? 'text-purple-500' :
                            data.type === 'transferLog' ? 'text-green-500' : 'text-gray-500'
                          }`}>
                            {data.type === 'transferLog' ? 'TRANSFER LOG' : data.type.toLocaleUpperCase()}
                          </span>
                        </h3>
                        <h3 className="font-medium text-sm text-gray-900">
                          {data.text}
                        </h3>
                        {data.type === 'transferLog' && (
                          <div className="text-xs text-gray-600 mt-1">
                            <div>From: <span className="font-medium">{data.exAssignedName}</span></div>
                            <div>To: <span className="font-medium">{data.newAssignedName}</span></div>
                          </div>
                        )}
                        {data.isDone !== undefined && (
                          <div className="text-xs text-gray-500 mt-1">
                            <span className={`Status ${getStatusStyles(data.isDone, data.isEdited).color}`}>
                              Status: {getStatusStyles(data.isDone, data.isEdited).text}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
          {(isLoadingFollowUps || isLoadingRemarks || isLoadingEmails || isLoadingTransferLogs) && (

            <div className="text-center py-2">

              <div className="text-gray-500 text-xs">Loading more...</div>

            </div>

          )}

          {!hasMoreFollowUps && followUps.length > 0 && !hasMoreRemarks && remarks.length > 0 && !hasMoreEmails && emails.length > 0 && !hasMoreTransferLogs && transferLogs.length > 0 && (

            <div className="text-center py-2">

              <div className="text-gray-500 text-xs">No more followups, remarks, emails, or transfer logs</div>

            </div>

          )}
        </div>
      </div>
    </div>
  );
};

export default FollowUps;